import React, { useState, useEffect, useRef } from "react";
import { FaChevronDown } from "react-icons/fa";

const ChevronDown = FaChevronDown as unknown as React.FC<
  { size: number } & React.SVGProps<SVGSVGElement>
>;

interface PriceRange {
  min: number;
  max: number;
}

interface PriceSliderDropdownProps {
  label: string;
  min: number;
  max: number;
  onSelect: (range: PriceRange) => void;
}

const PriceSliderDropdown: React.FC<PriceSliderDropdownProps> = ({
  label,
  min,
  max,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [priceRange, setPriceRange] = useState<PriceRange>({ min, max });
  const [maxError, setMaxError] = useState<string | null>(null); // State to track max error message
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.max(min, parseFloat(event.target.value));
    setPriceRange({ ...priceRange, min: newMin });
  };

  const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.min(max, parseFloat(event.target.value));
    if (newMax === 0) {
      setMaxError("Max price cannot be 0");
    } else {
      setMaxError(null);
    }
    setPriceRange({ ...priceRange, max: newMax });
  };

  const handleApply = () => {
    if (priceRange.max === 0) {
      setMaxError("Max price cannot be 0");
      return;
    }
    onSelect(priceRange);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="inline-flex items-center space-x-1.5 rounded-md border border-[#E2E8F0] bg-white px-4 py-1.5 text-sm font-medium text-[#0F172A] hover:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-200 transition"
      >
        <span>{label}</span>
        <ChevronDown
          size={12}
          className={`transform transition-transform duration-150 ${
            isOpen ? "rotate-180" : ""
          } text-[#0F172A]`}
        />
      </button>
      {isOpen && (
        <div className="absolute left-0 mt-2 min-w-[400px] bg-white rounded-xl shadow-lg border border-gray-200 z-10 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">{label}</h3>
          <div className="flex-col mb-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Min</label>
            </div>
            <div>
              <input
                type="number"
                value={priceRange.min}
                onChange={handleMinChange}
                min={min}
                max={priceRange.max}
                className="w-full border p-2 border-gray-300 rounded-md px-2 py-1 text-sm text-gray-700"
              />
            </div>
          </div>
          <div className="flex-col mb-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Max</label>
            </div>
            <div>
              <input
                type="number"
                value={priceRange.max}
                onChange={handleMaxChange}
                min={priceRange.min}
                max={max}
                className="w-full p-2 border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-700"
              />
              {maxError && (
                <p className="text-red-500 text-xs mt-1">{maxError}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleApply}
            className="w-full bg-pink-500 text-white font-medium py-2 rounded-md hover:bg-pink-600 transition-colors"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default PriceSliderDropdown;